/**
 * TimelineConnector — the vertical line linking one card in a proposal's
 * timeline to whatever renders beneath it: the next trigger card, or the first
 * action.
 *
 * Extracted so every card in the chain draws the *same* line instead of each
 * re-declaring the same Tailwind classes. Per FRONTEND_PATTERNS ("two
 * components that should look the same must share an actual component
 * implementation, not just the same Tailwind classes"), those class strings
 * would otherwise drift apart silently — this line is rendered from five
 * places across `TriggerPreview`, `CrmEventTriggerPreview` (a skeleton and a
 * real card each), and `ActionOverview`.
 *
 * Layout notes:
 * - `pl-[27px]` centres the 2px rule under the leading icon of the card above
 *   it (the card's padding plus half its `size-5`/`w-8` mark or avatar), so the
 *   line runs straight down through the whole chain.
 * - There is deliberately **no negative margin**. The `h-4` box sits in normal
 *   flow and fills exactly the space between one card's bottom edge and the
 *   next element's top edge — zero overlap, zero gap. An earlier `-mb-2`
 *   overlapped the following card to fake the attachment and was removed
 *   because it showed at the trigger-to-trigger and trigger-to-action
 *   junctions. Consequently a card that renders this connector must not also
 *   carry a bottom margin, or the gap comes back.
 * - `h-4` (16px) is not arbitrary: because this box *is* the whole gap, its
 *   height sets the spacing between connected cards, and it must equal the
 *   `gap-4` that MultiActionTimeline puts between action cards. At the previous
 *   `h-8` every junction with a line sat twice as far apart as every junction
 *   without one, so the trigger stack visibly drifted from the action stack
 *   below it. Change one and you must change the other.
 *
 * Purely decorative, so always `aria-hidden`. Each caller owns its own
 * visibility condition — there is deliberately no `show` prop, since the
 * gating differs per call site.
 */
export function TimelineConnector() {
  return (
    <div className='flex justify-start pl-[27px] h-4'>
      <div
        className='w-[2px] h-full bg-gray-300 dark:bg-gray-500 rounded-full'
        aria-hidden='true'
      />
    </div>
  );
}
